import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Checkbox,
  Chip,
  FormControlLabel,
  FormControlLabel as Label,
  Switch,
  TextField,
  CircularProgress,
  useTheme,
  alpha,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { getMessages } from "./api/newTelemetry";
import { ACCENT } from "./accent";

// Types worth charting: those with a TimeUS field and at least one other field.
const chartableTypes = (messageIndex) =>
  Object.entries(messageIndex || {})
    .filter(([, meta]) => (meta.fields || []).includes("TimeUS") && (meta.fields || []).length > 1)
    .map(([name]) => name)
    .sort();

/**
 * How many series can be plotted at once.
 *
 * Not a technical limit — a legibility one. Past half a dozen lines the graph
 * stops answering questions, and each extra type is another request.
 */
const MAX_SERIES = 6;

// Distinguishable on both themes. Order is the order series are picked in.
const COLOURS = [ACCENT, "#e5484d", "#30a46c", "#f5a524", "#8e4ec6", "#00a2c7"];

const key = (type, field) => `${type}.${field}`;

// Log values span volts, degrees and metres per second; a fixed number of
// decimals either drowns a small one in zeros or rounds it to nothing.
// Significant figures keep 0.01465 and 1528 both readable.
const compact = (v) => (typeof v === "number" ? Number(v.toPrecision(4)) : v);

/**
 * Per-series min/max, used both to normalise and to label the legend.
 *
 * A log's own scales are wildly different — GPS speed in the tens, battery
 * volts in the twenties, roll in single-digit degrees either side of zero — so
 * this is what makes plotting them together mean anything.
 */
const rangeOf = (rows, k) => {
  const values = rows.map((r) => r[`${k}__raw`]).filter((v) => typeof v === "number");
  if (!values.length) return null;
  return { min: Math.min(...values), max: Math.max(...values) };
};

// fetchMessages defaults to the authed API; the public detail page passes the
// unauthenticated getPublicMessages so the same chart works on both.
const LogCharts = ({ log, fetchMessages = getMessages }) => {
  const theme = useTheme();
  const types = useMemo(() => chartableTypes(log?.message_index), [log]);

  const [selected, setSelected] = useState([]); // [{ type, field }]
  const [expanded, setExpanded] = useState("");
  const [filter, setFilter] = useState("");
  const [normalise, setNormalise] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Open on something worth seeing rather than an empty graph.
  useEffect(() => {
    if (!types.length) return;
    const preferred = ["ATT", "GPS", "BAT", "VIBE"].find((t) => types.includes(t));
    const first = preferred || types[0];
    const field = (log?.message_index?.[first]?.fields || []).find((f) => f !== "TimeUS");
    setExpanded(first);
    if (field) setSelected((cur) => (cur.length ? cur : [{ type: first, field }]));
  }, [types, log]);

  const fieldsOf = (type) => (log?.message_index?.[type]?.fields || []).filter((f) => f !== "TimeUS");

  const isOn = (type, field) => selected.some((s) => s.type === type && s.field === field);

  const toggle = (type, field) =>
    setSelected((cur) => {
      const without = cur.filter((s) => !(s.type === type && s.field === field));
      if (without.length !== cur.length) return without;
      if (cur.length >= MAX_SERIES) return cur;
      return [...cur, { type, field }];
    });

  const visibleTypes = useMemo(() => {
    const q = filter.trim().toUpperCase();
    return q ? types.filter((t) => t.includes(q)) : types;
  }, [types, filter]);

  // One request per message type carrying all of that type's picked fields —
  // the API takes a field list, so ticking three ATT fields is one fetch, not
  // three. `signature` keeps this from re-firing on unrelated re-renders.
  const signature = selected.map((s) => key(s.type, s.field)).sort().join(",");

  useEffect(() => {
    if (!selected.length || !log?.id) {
      setRows([]);
      return undefined;
    }
    const byType = selected.reduce((acc, s) => {
      (acc[s.type] = acc[s.type] || []).push(s.field);
      return acc;
    }, {});

    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all(
      Object.entries(byType).map(([type, fields]) =>
        fetchMessages(log.id, { type, fields, maxPoints: 800 }).then((data) => ({ type, fields, data }))
      )
    )
      .then((results) => {
        if (cancelled) return;
        // One clock for every series. Each type reports its own first sample,
        // so without a shared origin two series drift apart on the x axis.
        const t0 = Math.min(
          ...results.map((r) => r.data.points?.[0]?.TimeUS ?? Infinity).filter(Number.isFinite)
        );
        const merged = new Map();
        results.forEach(({ type, fields, data }) => {
          (data.points || []).forEach((p) => {
            const t = Math.round(((p.TimeUS - t0) / 1e6) * 1000) / 1000;
            const row = merged.get(t) || { t };
            fields.forEach((f) => {
              if (typeof p[f] === "number") row[`${key(type, f)}__raw`] = p[f];
            });
            merged.set(t, row);
          });
        });
        setRows([...merged.values()].sort((a, b) => a.t - b.t));
      })
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
    // fetchMessages is a prop with a module-level default; a caller passing an
    // inline function would re-fetch on every render if it were listed here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, log?.id]);

  // Ranges drive the legend labels and, when normalising, the plotted value.
  const ranges = useMemo(() => {
    const out = {};
    selected.forEach((s) => {
      out[key(s.type, s.field)] = rangeOf(rows, key(s.type, s.field));
    });
    return out;
  }, [rows, selected]);

  const plotted = useMemo(() => {
    if (!normalise) {
      return rows.map((r) => {
        const row = { t: r.t };
        selected.forEach((s) => {
          const k = key(s.type, s.field);
          row[k] = r[`${k}__raw`];
          row[`${k}__raw`] = r[`${k}__raw`];
        });
        return row;
      });
    }
    return rows.map((r) => {
      const row = { t: r.t };
      selected.forEach((s) => {
        const k = key(s.type, s.field);
        const raw = r[`${k}__raw`];
        const range = ranges[k];
        row[`${k}__raw`] = raw;
        // A flat series has no span to scale by; park it mid-axis rather than
        // dividing by zero.
        row[k] =
          typeof raw !== "number" || !range
            ? undefined
            : range.max === range.min
              ? 0.5
              : (raw - range.min) / (range.max - range.min);
      });
      return row;
    });
  }, [rows, selected, ranges, normalise]);

  if (!types.length) {
    return (
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
        No chartable data in this log.
      </Typography>
    );
  }

  const border = `1px solid ${alpha(theme.palette.divider, 0.6)}`;

  return (
    <Box sx={{ display: "flex", gap: 2, flexWrap: { xs: "wrap", md: "nowrap" }, alignItems: "flex-start" }}>
      {/* The log's own table of contents: every message type it recorded, and
          every field inside the one you open. */}
      <Box sx={{ width: { xs: "100%", md: 260 }, flexShrink: 0, border, borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ p: 1, borderBottom: border }}>
          <TextField
            size="small"
            fullWidth
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={`Filter ${types.length} messages…`}
          />
        </Box>
        <Box sx={{ maxHeight: 340, overflow: "auto" }}>
          {visibleTypes.map((t) => {
            const open = expanded === t;
            const picked = selected.filter((s) => s.type === t).length;
            return (
              <Box key={t}>
                <Box
                  component="button"
                  type="button"
                  onClick={() => setExpanded(open ? "" : t)}
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1,
                    py: 0.6,
                    background: "none",
                    border: 0,
                    font: "inherit",
                    fontSize: 13,
                    cursor: "pointer",
                    color: picked ? ACCENT : theme.palette.text.primary,
                    fontWeight: picked ? 700 : 400,
                    "&:hover": { backgroundColor: alpha(theme.palette.text.primary, 0.04) },
                  }}
                >
                  {open ? <ExpandMoreIcon sx={{ fontSize: 16 }} /> : <ChevronRightIcon sx={{ fontSize: 16 }} />}
                  {t}
                  {picked > 0 && ` (${picked})`}
                </Box>
                {open && (
                  <Box sx={{ pl: 3, pb: 0.5 }}>
                    {fieldsOf(t).map((f) => {
                      const on = isOn(t, f);
                      return (
                        <FormControlLabel
                          key={f}
                          sx={{ display: "flex", m: 0, "& .MuiFormControlLabel-label": { fontSize: 12.5 } }}
                          control={
                            <Checkbox
                              size="small"
                              checked={on}
                              // A full graph must not silently ignore a click.
                              disabled={!on && selected.length >= MAX_SERIES}
                              onChange={() => toggle(t, f)}
                              sx={{ py: 0.25, color: theme.palette.text.secondary, "&.Mui-checked": { color: ACCENT } }}
                            />
                          }
                          label={f}
                        />
                      );
                    })}
                  </Box>
                )}
              </Box>
            );
          })}
          {visibleTypes.length === 0 && (
            <Typography variant="caption" sx={{ display: "block", p: 1.5, color: theme.palette.text.secondary }}>
              No message type matches “{filter}”.
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", mb: 1.5 }}>
          {selected.map((s, i) => {
            const k = key(s.type, s.field);
            const range = ranges[k];
            return (
              <Chip
                key={k}
                size="small"
                onDelete={() => toggle(s.type, s.field)}
                label={range ? `${k}  ${compact(range.min)} … ${compact(range.max)}` : k}
                sx={{
                  fontWeight: 600,
                  color: COLOURS[i % COLOURS.length],
                  borderColor: COLOURS[i % COLOURS.length],
                  backgroundColor: alpha(COLOURS[i % COLOURS.length], 0.12),
                  border: `1px solid ${COLOURS[i % COLOURS.length]}`,
                  "& .MuiChip-deleteIcon": { color: COLOURS[i % COLOURS.length] },
                }}
              />
            );
          })}
          {selected.length === 0 && (
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              Tick a field on the left to plot it.
            </Typography>
          )}
          {selected.length >= MAX_SERIES && (
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              {MAX_SERIES} is the limit — untick one to add another.
            </Typography>
          )}
          <Label
            sx={{ ml: "auto", mr: 0, "& .MuiFormControlLabel-label": { fontSize: 12.5 } }}
            control={<Switch size="small" checked={normalise} onChange={(e) => setNormalise(e.target.checked)} />}
            label="Normalise"
          />
        </Box>

        {/* Raw by default, because a real axis is the honest one. Normalise is
            for the mixed-unit case: 25 V and 3° of roll on one raw axis makes
            the roll a flat line at the bottom. Tooltips always read real
            values, whichever mode is on. */}
        <Box sx={{ height: 320, position: "relative" }}>
          {loading && (
            <Box sx={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", zIndex: 1 }}>
              <CircularProgress color="secondary" size={24} />
            </Box>
          )}
          {error ? (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={plotted} margin={{ top: 8, right: 12, bottom: 8, left: 0 }}>
                <CartesianGrid stroke={alpha(theme.palette.divider, 0.4)} vertical={false} />
                <XAxis
                  dataKey="t"
                  tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                  tickFormatter={(v) => `${Math.round(v)}s`}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                  domain={normalise ? [0, 1] : ["auto", "auto"]}
                  width={56}
                />
                <Tooltip
                  contentStyle={{
                    background: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    fontSize: 12,
                  }}
                  labelFormatter={(v) => `${Number(v).toFixed(2)} s`}
                  formatter={(value, name, item) => [compact(item?.payload?.[`${name}__raw`] ?? value), name]}
                />
                {selected.map((s, i) => (
                  <Line
                    key={key(s.type, s.field)}
                    type="monotone"
                    dataKey={key(s.type, s.field)}
                    name={key(s.type, s.field)}
                    stroke={COLOURS[i % COLOURS.length]}
                    dot={false}
                    strokeWidth={1.6}
                    isAnimationActive={false}
                    // Types log at different rates, so the merged rows are
                    // sparse: without this every series draws as dashes.
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default LogCharts;
